import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import styles from "./DefaultVariantSelector.module.css";
import { Button, Modal, Select, Text } from "@shopify/polaris";
import useIsMobile from "../../hooks/useIsMobile";
import { AppConfigContext } from "../../context/AppConfigContext";
import { VolumePreview } from "../../apps/Bundles/VolumeDiscount/VolumeBundle/VolumeBundle";
import { BundleTypes, getProductsDynamicallyAndReturnNewProductsArray } from "../../apps/Bundles/constants";
import { DefaultVariantType, ProductObj, VariantsObj, VolumeConfigInterface } from "../../apps/Bundles/Types";
import { EditIcon } from "@shopify/polaris-icons";
interface DefaultVariantSelectorTypes {
    openDefaultVariantSelector: boolean;
    setOpenDefaultVariantSelector: (val: boolean) => void;
    bundleConfig: VolumeConfigInterface;
    activeQuantity: number;
    setActiveQuantity: Dispatch<SetStateAction<number>>;
    activeProduct: ProductObj | null;
    setActiveProduct: Dispatch<SetStateAction<ProductObj | null>>;
    defaultVariantTypes: { value: DefaultVariantType[]; onChange: (value: DefaultVariantType[]) => void };
    allowVariantSelection: boolean;
    resourcePickerQuery: string;
}

const OPTION_SEPARATOR = "|kefi|";

export const VariantSelector = ({
    variants = [],
    selected,
    handleChange = () => {},
    productCardIndex,
}: {
    variants: VariantsObj[];
    selected: VariantsObj[];
    handleChange: any;
    productCardIndex: number;
}) => {
    const handleOptionChange = (value: string) => {
        const updatedSelectedVariants = [...selected];

        if (value === "default") {
            // @ts-expect-error no needed to add everything here
            updatedSelectedVariants[productCardIndex] = {
                id: "default",
                title: "Default",
            };

            handleChange(updatedSelectedVariants);
            return;
        }

        const selectedOptionsArray =
            value
                ?.split(OPTION_SEPARATOR)
                ?.map((val) => val.trim())
                ?.filter((val) => val !== "") || [];
        const matchingVariant = findSelectedVariant(selectedOptionsArray);
        if (matchingVariant) {
            updatedSelectedVariants[productCardIndex] = matchingVariant;
            handleChange(updatedSelectedVariants);
        }
    };

    const findSelectedVariant = (selectedOptionsArray: string[]) => {
        for (const variant of variants) {
            const hasAllSelectedOptions = selectedOptionsArray.every(
                (selectedValue, i) => variant?.selectedOptions?.[i].value === selectedValue,
            );
            if (hasAllSelectedOptions) {
                return {
                    ...variant,
                };
            }
        }
        return null;
    };

    const selectedValue =
        selected?.[productCardIndex]?.id === "default"
            ? "default"
            : selected?.[productCardIndex]?.selectedOptions?.map((o: any) => o.value).join(OPTION_SEPARATOR) ||
              "default";

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Text
                as="h4"
                numeric
                children={<span style={{ minWidth: 40, width: 40 }}>{productCardIndex + 1}</span>}
            ></Text>
            <div style={{ minWidth: "60%" }}>
                <Select
                    label=""
                    labelHidden
                    onChange={(value) => {
                        handleOptionChange(value);
                    }}
                    value={selectedValue}
                    options={[
                        { label: "Default", value: "default" },
                        ...variants.map((value: any) => ({
                            label: value.title,
                            value: value?.selectedOptions?.map((o: any) => o.value).join(OPTION_SEPARATOR),
                        })),
                    ]}
                />
            </div>
        </div>
    );
};

export const useProductWithQuantity = (product: ProductObj | null) => {
    const productWithQuantity = useMemo(() => {
        if (!product) return null;

        return {
            ...product,
            variants: product.variants.map((variant: any) => ({
                ...variant,
                availableForSale: true,
                quantityAvailable: 1000,
            })),
        };
    }, [product]);

    return productWithQuantity;
};

const DefaultVariantSelector = ({
    openDefaultVariantSelector,
    setOpenDefaultVariantSelector,
    bundleConfig,
    activeQuantity,
    setActiveQuantity,
    activeProduct,
    setActiveProduct,
    defaultVariantTypes,
    allowVariantSelection = false,
    resourcePickerQuery,
}: DefaultVariantSelectorTypes) => {
    const { isMobile } = useIsMobile();
    const appConfigContext = useContext(AppConfigContext);
    const { sft } = appConfigContext;
    const [selectedVariants, setSelectedVariants] = useState<any[]>([]);

    const productWithQuantity = useProductWithQuantity(activeProduct);

    useEffect(() => {
        if (!productWithQuantity) return;

        const variants = productWithQuantity.variants;
        const quantity = activeQuantity;

        const existing = defaultVariantTypes.value?.find(
            (item) => item.productID === productWithQuantity.id && item.variantIDs.length === quantity,
        );

        if (existing) {
            const hydrated = existing.variantIDs.map((gid: string | null) => {
                const variant = variants.find((v: any) => v.id === gid);
                return variant || null;
            });

            setSelectedVariants(hydrated);
        } else {
            const initial = Array.from({ length: quantity }).map(() => null);
            setSelectedVariants(initial);
        }
    }, [productWithQuantity, activeQuantity]);

    const handleClose = () => {
        setOpenDefaultVariantSelector(false);
    };

    const handleSelectProduct = async () => {
        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: false,
            action: "select",
            filter: {
                query: resourcePickerQuery,
                variants: allowVariantSelection,
            },
        });

        if (selected) {
            const product = selected[0] as unknown as ProductObj;
            try {
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(
                    selected,
                    sft,
                    false,
                    bundleConfig.bundle_type,
                );
                setActiveProduct(newProductsArr[0]);
            } catch (error) {
                setActiveProduct(product);
            }
        }
    };

    const handleVariantChange = (updatedSelected: any[]) => {
        if (!productWithQuantity) return;

        setSelectedVariants(updatedSelected);

        const updated = [...defaultVariantTypes.value];
        const quantity = activeQuantity;

        const variantIDs = Array.from({ length: quantity }).map((_, i) => {
            const variant = updatedSelected[i];
            if (!variant || variant.id === "default") return null;

            return variant.id;
        });

        const existingIndex = updated.findIndex(
            (item) => item.productID === productWithQuantity.id && item.variantIDs.length === quantity,
        );

        if (existingIndex !== -1) {
            updated[existingIndex] = {
                productID: productWithQuantity.id,
                variantIDs,
            };
        } else {
            updated.push({
                productID: productWithQuantity.id,
                variantIDs,
            });
        }

        defaultVariantTypes.onChange(updated);
    };

    return (
        <div className={styles.modalWrapper}>
            <Modal
                open={openDefaultVariantSelector}
                onScrolledToBottom={() => {}}
                sectioned
                onClose={handleClose}
                title="Set default variants of products"
                limitHeight={false}
                size="large"
                instant
                primaryAction={{
                    content: "Alright",
                    onAction: handleClose,
                }}
            >
                <div className={styles.modalContainer}>
                    <div className={isMobile ? styles.layoutColumn : styles.layoutRow}>
                        {/* Left Panel */}
                        <div className={styles.editorPanel}>
                            <Text variant="headingMd" as="h3">
                                Product Selector
                            </Text>

                            <div style={{ marginTop: 10, marginBottom: 15 }}>
                                <Button icon={EditIcon} onClick={handleSelectProduct}>
                                    Select Product
                                </Button>
                            </div>

                            {productWithQuantity && (
                                <div
                                    style={{
                                        marginTop: 10,
                                        marginBottom: 15,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 6,
                                    }}
                                >
                                    <Text as="h2" variant="headingMd">
                                        Product - {productWithQuantity.title}
                                    </Text>

                                    {productWithQuantity.variants.length > 1 && (
                                        <Text as="h2" variant="bodySm">
                                            Selecting
                                            <Text as="span" variant="headingSm">
                                                &nbsp;Default&nbsp;
                                            </Text>
                                            will automatically select the first available variant on store.
                                        </Text>
                                    )}
                                </div>
                            )}

                            {productWithQuantity &&
                                productWithQuantity.variants.length > 1 &&
                                Array.from({ length: activeQuantity }).map((_, i) => (
                                    <VariantSelector
                                        variants={productWithQuantity.variants || []}
                                        selected={selectedVariants}
                                        handleChange={handleVariantChange}
                                        productCardIndex={i}
                                    />
                                ))}
                        </div>

                        {/* Right Panel */}
                        <div className={styles.previewPanel}>
                            {(() => {
                                switch (bundleConfig?.bundle_type) {
                                    case BundleTypes.VOLUME_DISCOUNT:
                                        return (
                                            <VolumePreview
                                                pId={0}
                                                appConfigContext={appConfigContext}
                                                bundleConfig={{
                                                    ...bundleConfig,
                                                    products: productWithQuantity ? [productWithQuantity] : [],
                                                    source: "defaultVariantModal",
                                                }}
                                                setActiveQuantity={setActiveQuantity}
                                            />
                                        );

                                    default:
                                        return null;
                                }
                            })()}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DefaultVariantSelector;
